import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  updateDoc,
  doc,
  deleteDoc,
  serverTimestamp,
  onSnapshot,
} from "firebase/firestore";
import { FSdb } from "../config/firebase";

/**
 * Notification Service
 * Handles all notification-related operations for contributions, suggestions, and questions
 */

export const notificationService = {
  /**
   * Create a notification when someone comments on a post/suggestion
   * @param {Object} params - Notification parameters
   * @param {string} params.postOwnerUid - UID of the post/suggestion owner
   * @param {string} params.commenterId - UID of the person commenting
   * @param {string} params.commenterName - Name of the person commenting
   * @param {string} params.postId - ID of the post/contribution/question
   * @param {string} params.postType - Type of post: 'contribution', 'suggestion', 'question'
   * @param {string} params.postTitle - Title or preview of the post
   * @param {string} params.commentContent - Content of the comment
   * @returns {Promise<string>} - Notification ID
   */
  createNotification: async ({
    postOwnerUid,
    commenterId,
    commenterName,
    postId,
    postType,
    postTitle,
    commentContent,
  }) => {
    try {
      // Don't notify user about their own comments
      if (postOwnerUid === commenterId) {
        return null;
      }

      const notificationsRef = collection(FSdb, "notifications");
      const notificationDoc = await addDoc(notificationsRef, {
        recipientUid: postOwnerUid,
        commenterId: commenterId,
        commenterName: commenterName,
        postId: postId,
        postType: postType,
        postTitle: postTitle,
        commentContent: commentContent,
        message: `${commenterName} commented on your ${postType}`,
        isRead: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      return notificationDoc.id;
    } catch (error) {
      console.error("Error creating notification:", error);
      throw error;
    }
  },

  /**
   * Get all notifications for a user
   * @param {string} uid - User ID
   * @param {boolean} unreadOnly - Get only unread notifications
   * @returns {Promise<Array>} - Array of notifications
   */
  getUserNotifications: async (uid, unreadOnly = false) => {
    try {
      let q;
      if (unreadOnly) {
        q = query(
          collection(FSdb, "notifications"),
          where("recipientUid", "==", uid),
          where("isRead", "==", false)
          // orderBy removed - will add index later
        );
      } else {
        q = query(
          collection(FSdb, "notifications"),
          where("recipientUid", "==", uid)
          // orderBy removed - will add index later
        );
      }

      const snapshot = await getDocs(q);
      const notifications = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      
      // Sort manually by createdAt descending
      return notifications.sort((a, b) => {
        const timeA = a.createdAt?.seconds || 0;
        const timeB = b.createdAt?.seconds || 0;
        return timeB - timeA;
      });
    } catch (error) {
      console.error("Error fetching notifications:", error);
      throw error;
    }
  },

  /**
   * Get notification count for a user
   * @param {string} uid - User ID
   * @returns {Promise<number>} - Count of unread notifications
   */
  getUnreadNotificationCount: async (uid) => {
    try {
      const q = query(
        collection(FSdb, "notifications"),
        where("recipientUid", "==", uid),
        where("isRead", "==", false)
      );

      const snapshot = await getDocs(q);
      return snapshot.size;
    } catch (error) {
      console.error("Error fetching notification count:", error);
      throw error;
    }
  },

  /**
   * Mark a notification as read
   * @param {string} notificationId - Notification ID
   * @returns {Promise<void>}
   */
  markAsRead: async (notificationId) => {
    try {
      const notifRef = doc(FSdb, "notifications", notificationId);
      await updateDoc(notifRef, {
        isRead: true,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error("Error marking notification as read:", error);
      throw error;
    }
  },

  /**
   * Mark all notifications as read for a user
   * @param {string} uid - User ID
   * @returns {Promise<void>}
   */
  markAllAsRead: async (uid) => {
    try {
      const q = query(
        collection(FSdb, "notifications"),
        where("recipientUid", "==", uid),
        where("isRead", "==", false)
      );

      const snapshot = await getDocs(q);
      const updatePromises = snapshot.docs.map((doc) =>
        updateDoc(doc.ref, {
          isRead: true,
          updatedAt: serverTimestamp(),
        })
      );

      await Promise.all(updatePromises);
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
      throw error;
    }
  },

  /**
   * Delete a notification
   * @param {string} notificationId - Notification ID
   * @returns {Promise<void>}
   */
  deleteNotification: async (notificationId) => {
    try {
      await deleteDoc(doc(FSdb, "notifications", notificationId));
    } catch (error) {
      console.error("Error deleting notification:", error);
      throw error;
    }
  },

  /**
   * Delete all notifications for a user
   * @param {string} uid - User ID
   * @returns {Promise<void>}
   */
  deleteAllNotifications: async (uid) => {
    try {
      const q = query(
        collection(FSdb, "notifications"),
        where("recipientUid", "==", uid)
      );

      const snapshot = await getDocs(q);
      const deletePromises = snapshot.docs.map((doc) => deleteDoc(doc.ref));

      await Promise.all(deletePromises);
    } catch (error) {
      console.error("Error deleting all notifications:", error);
      throw error;
    }
  },

  /**
   * Listen to real-time notifications for a user
   * @param {string} uid - User ID
   * @param {Function} onUpdate - Callback function when notifications change
   * @returns {Function} - Unsubscribe function
   */
  subscribeToNotifications: (uid, onUpdate) => {
    try {
      if (!uid) {
        console.error("❌ No uid provided to subscribeToNotifications");
        return () => {};
      }

      const q = query(
        collection(FSdb, "notifications"),
        where("recipientUid", "==", uid)
      );

      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const notifications = snapshot.docs
            .map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }))
            .sort((a, b) => {
              const timeA = a.createdAt?.seconds || 0;
              const timeB = b.createdAt?.seconds || 0;
              return timeB - timeA;
            });
          onUpdate(notifications);
        },
        (error) => {
          console.error("❌ Error in onSnapshot:", error);
          console.error("❌ Error code:", error.code);
          console.error("❌ Error message:", error.message);
          // Call onUpdate with empty array on error
          onUpdate([]);
        }
      );

      return unsubscribe;
    } catch (error) {
      console.error("❌ subscribeToNotifications Error:", error);
      // Return a dummy unsubscribe function
      return () => {};
    }
  },

  /**
   * Get notifications for a specific post
   * @param {string} postId - Post ID
   * @param {string} postType - Type of post: 'contribution', 'suggestion', 'question'
   * @returns {Promise<Array>} - Array of notifications
   */
  getPostNotifications: async (postId, postType) => {
    try {
      const q = query(
        collection(FSdb, "notifications"),
        where("postId", "==", postId),
        where("postType", "==", postType)
        // orderBy removed - will add index later
      );

      const snapshot = await getDocs(q);
      const notifications = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      
      // Sort manually by createdAt descending
      return notifications.sort((a, b) => {
        const timeA = a.createdAt?.seconds || 0;
        const timeB = b.createdAt?.seconds || 0;
        return timeB - timeA;
      });
    } catch (error) {
      console.error("Error fetching post notifications:", error);
      throw error;
    }
  },
};

export default notificationService;

